import { Anchor, Badge, Box, Button, Group, Popover, Select, Stack, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { IconAdjustmentsHorizontal, IconArrowRight, IconChevronDown, IconCpu } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { LANGUAGES } from "@passio/api-client";
import type { ModelPick, ModelPickKind, PublishedRoute } from "@passio/easyai-client";
import { aiRoute, modelOptions, routeModels, setRouteModel, setRouteModels, useRouteModels, useServedModels } from "../lib/models";
import { COST_LEVELS, TIER_COLORS, useModelCatalog, useModelInfo, type ModelInfo } from "../lib/modelInfo";
import { openClientSettings, useClientHeaders } from "../lib/clientSettings";
import { fmtAccuracy, PICK_KINDS, PICKS, picksFor, testedModel, useRouteTests } from "../lib/routeTests";

const ROUTE_DEFAULT = "__default";

export function TierBadge({ tier, size = "xs" }: { tier: string; size?: "xs" | "sm" }) {
  return (
    <Badge size={size} color={TIER_COLORS[tier] ?? "gray"} variant="light" tt="capitalize">
      {tier}
    </Badge>
  );
}

/** 1-5 as filled dots. */
export function RatingDots({ value, color = "var(--pd-violet)", label }: { value: number; color?: string; label?: string }) {
  return (
    <Group gap={3} wrap="nowrap" aria-label={label ? `${label} ${value} of 5` : `${value} of 5`} role="img">
      {[1, 2, 3, 4, 5].map((i) => (
        <Box key={i} w={7} h={7} style={{ borderRadius: 99, background: i <= value ? color : "var(--pd-sand)" }} />
      ))}
    </Group>
  );
}

/** Cost 1-5 as $ signs (the unfilled ones dimmed), optionally with its word ("Premium"). */
export function CostMeter({ value, fz = 12, withLabel = false }: { value: number; fz?: number; withLabel?: boolean }) {
  const label = COST_LEVELS[value]?.label;
  return (
    <Group component="span" gap={6} wrap="nowrap" aria-label={`${label ?? ""} cost, ${value} of 5`}>
      <Text component="span" fz={fz} fw={700} ff="monospace" style={{ letterSpacing: 1 }}>
        <span style={{ color: "var(--pd-green)" }}>{"$".repeat(value)}</span>
        <span style={{ color: "var(--pd-sand)" }}>{"$".repeat(5 - value)}</span>
      </Text>
      {withLabel && label && (
        <Text component="span" fz={fz} fw={600}>
          {label}
        </Text>
      )}
    </Group>
  );
}

export function PickBadge({ kind, size = "xs" }: { kind: ModelPickKind; size?: "xs" | "sm" }) {
  return (
    <Badge size={size} color={PICKS[kind].color} variant="filled">
      {PICKS[kind].label}
    </Badge>
  );
}

function ModelOption({ m, tests }: { m: ModelInfo; tests?: PublishedRoute }) {
  const picks = picksFor(tests, m.tag);
  const accuracy = fmtAccuracy(testedModel(tests, m.tag)?.accuracy);
  return (
    <Stack gap={3} w="100%">
      <Group gap={6} justify="space-between" wrap="nowrap">
        <Group gap={6} wrap="nowrap">
          <Text fz="sm" fw={600}>
            {m.name}
          </Text>
          <TierBadge tier={m.tier} />
        </Group>
        <CostMeter value={m.cost} fz={11} />
      </Group>
      <Group gap={10} wrap="nowrap">
        <Text fz={11} c="dimmed" ff="monospace" style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
          {m.tag}
        </Text>
        <Group gap={4} wrap="nowrap">
          <Text fz={10} c="dimmed">
            smart
          </Text>
          <RatingDots value={m.intelligence} label="Intelligence" />
        </Group>
        <Group gap={4} wrap="nowrap">
          <Text fz={10} c="dimmed">
            fast
          </Text>
          <RatingDots value={m.speed} color="var(--pd-lime)" label="Speed" />
        </Group>
      </Group>
      {(!m.reliable || m.visionOnly || m.zdr || picks.length > 0 || accuracy) && (
        <Group gap={4}>
          {picks.map((k) => (
            <PickBadge key={k} kind={k} />
          ))}
          {accuracy && (
            <Badge size="xs" color="dark" variant="light">
              {accuracy} accurate
            </Badge>
          )}
          {!m.reliable && (
            <Badge size="xs" color="orange" variant="light">
              preview
            </Badge>
          )}
          {m.visionOnly && (
            <Badge size="xs" color="violet" variant="outline">
              images only
            </Badge>
          )}
          {m.zdr && (
            <Badge size="xs" color="gray" variant="light">
              ZDR
            </Badge>
          )}
        </Group>
      )}
    </Stack>
  );
}

/** Model select for one AI route. Empty value = the route's own default model. */
export function ModelSelect({
  routeId,
  value,
  onChange,
  label,
  size = "sm",
  details = true,
  inPopover = false,
}: {
  routeId: string;
  value: string;
  onChange: (tag: string) => void;
  label?: string;
  size?: "xs" | "sm";
  details?: boolean;
  inPopover?: boolean;
}) {
  const api = useModelCatalog();
  const { byTag } = useModelInfo();
  const tests = useRouteTests().byRoute.get(routeId);
  const served = useServedModels()[routeId];
  const selected = byTag.get(value);
  const name = (tag: string) => byTag.get(tag)?.name ?? tag;
  const data = [{ value: ROUTE_DEFAULT, label: "Route default" }, ...(value && !selected ? [{ value, label: value }] : []), ...modelOptions(api.data ?? [], routeId)];

  let note: string | null = null;
  if (api.error) note = `Couldn't load the model list (${(api.error as Error).message}).`;
  else if (details && selected) note = `${selected.provider} · ${COST_LEVELS[selected.cost]?.label ?? ""} cost${selected.description ? ` · ${selected.description}` : ""}`;
  if (!api.error && served && served !== value) note = `${note ? `${note} · ` : ""}last call used ${name(served)}`;

  return (
    <Stack gap={2}>
      <Select
        size={size}
        label={label}
        searchable
        data={data}
        value={value || ROUTE_DEFAULT}
        onChange={(v) => onChange(!v || v === ROUTE_DEFAULT ? "" : v)}
        allowDeselect={false}
        nothingFoundMessage="No matching model"
        maxDropdownHeight={340}
        comboboxProps={inPopover ? { withinPortal: false } : undefined}
        renderOption={({ option }) => {
          const m = byTag.get(option.value);
          return m ? <ModelOption m={m} tests={tests} /> : <Text fz="sm">{option.label}</Text>;
        }}
      />
      {note && (
        <Text fz={11} c={api.error ? "red" : "dimmed"}>
          {note}
        </Text>
      )}
      {tests && <RoutePicks tests={tests} value={value} onChange={onChange} />}
    </Stack>
  );
}

/** One-click Recommended / High quality / Budget choices from Passio's tests of this route. */
function RoutePicks({ tests, value, onChange }: { tests: PublishedRoute; value: string; onChange: (tag: string) => void }) {
  const { byTag } = useModelInfo();
  const picks = PICK_KINDS.map((k) => [k, tests.picks?.[k]] as const).filter((e): e is [ModelPickKind, ModelPick] => !!e[1]?.model && byTag.has(e[1].model));
  if (!picks.length) return null;
  return (
    <Group gap={6} mt={2}>
      <Tooltip label={`Passio tested this route on ${tests.cases} cases, ${tests.repeats}× each, on ${new Date(tests.tested_at).toLocaleDateString()}.`} multiline w={260}>
        <Text fz={11} c="dimmed" style={{ cursor: "help" }}>
          Tested picks:
        </Text>
      </Tooltip>
      {picks.map(([k, p]) => (
        <Tooltip key={k} label={`${PICKS[k].summary}${p.why ? ` ${p.why}` : ""}`} multiline w={260}>
          <UnstyledButton onClick={() => onChange(p.model)} aria-label={`Use ${PICKS[k].label.toLowerCase()} model`}>
            <Badge size="sm" color={PICKS[k].color} variant={value === p.model ? "filled" : "light"} tt="none" style={{ cursor: "pointer" }}>
              {PICKS[k].label}: {byTag.get(p.model)!.name}
              {p.accuracy != null ? ` · ${fmtAccuracy(p.accuracy)}` : ""}
            </Badge>
          </UnstyledButton>
        </Tooltip>
      ))}
    </Group>
  );
}

/** One line summarising Client settings, with a button that opens them. */
export function ClientSettingsHint() {
  const h = useClientHeaders();
  const lang = LANGUAGES.find((l) => l.value === (h.localizationIso ?? "en"))?.label ?? h.localizationIso;
  const filters = Object.keys(h.searchConfig.toJSON()).length;
  const summary = [lang, h.productLocalization, filters ? `${filters} data filter${filters > 1 ? "s" : ""}` : "no data filters"].filter(Boolean).join(" · ");
  return (
    <Tooltip label="Language, regional food database and data-source filters are request headers sent with every call. Change them in Client settings." multiline w={280}>
      <UnstyledButton onClick={openClientSettings} aria-label="Open client settings">
        <Group gap={6} wrap="nowrap">
          <IconAdjustmentsHorizontal size={14} color="var(--mantine-color-dimmed)" />
          <Text fz={12} c="dimmed">
            {summary}
          </Text>
          <Text fz={12} c="violet.6" fw={600}>
            Client settings
          </Text>
        </Group>
      </UnstyledButton>
    </Tooltip>
  );
}

/** Page-header card: choose the model for each AI route this page calls, plus a pointer to Client settings. */
export function RouteModelPicker({ routes }: { routes: string[] }) {
  const models = useRouteModels();
  const served = useServedModels();
  const { byTag } = useModelInfo();
  const custom = routes.filter((id) => models[id]);
  const single = routes.length === 1 ? routes[0]! : null;
  const shownTag = single ? models[single] || served[single] : custom.length === 1 ? models[custom[0]!] : undefined;
  const shown = shownTag ? byTag.get(shownTag) : undefined;
  const title = single
    ? shown?.name ?? (models[single] || (served[single] ? served[single] : "Route default"))
    : custom.length
      ? `${custom.length} of ${routes.length} routes custom`
      : "Route defaults";
  const isDefault = !custom.length;

  return (
    <Stack gap={6} align="flex-end">
      <Popover width={420} position="bottom-end" shadow="lg" withArrow radius="md">
        <Popover.Target>
          <UnstyledButton
            aria-label="Choose AI model"
            style={{
              borderRadius: 14,
              padding: 1.5,
              background: isDefault ? "linear-gradient(120deg, var(--pd-violet), var(--pd-lime))" : "var(--pd-violet)",
              boxShadow: "0 6px 20px -10px rgba(140,108,251,.7)",
            }}
          >
            <Group gap="sm" wrap="nowrap" px="md" py={9} style={{ background: "#fff", borderRadius: 12.5, minWidth: 260 }}>
              <Box p={7} style={{ background: "var(--pd-ink)", color: "var(--pd-lime)", borderRadius: 10, display: "inline-flex" }}>
                <IconCpu size={20} />
              </Box>
              <Stack gap={1} style={{ flex: 1, minWidth: 0 }}>
                <Group gap={6} wrap="nowrap">
                  <Text fz={10} fw={700} c="dimmed" tt="uppercase" style={{ letterSpacing: "0.07em" }}>
                    AI model
                  </Text>
                  {shown && <TierBadge tier={shown.tier} />}
                  {!isDefault && (
                    <Badge size="xs" color="violet" variant="filled">
                      custom
                    </Badge>
                  )}
                </Group>
                <Text fz="sm" fw={700} truncate>
                  {title}
                  {single && !models[single] && shown ? (
                    <Text span c="dimmed" fw={500} fz="xs">
                      {" (default)"}
                    </Text>
                  ) : null}
                </Text>
                <Text fz={11} c="dimmed">
                  Tune for cost, speed or accuracy
                </Text>
              </Stack>
              <IconChevronDown size={16} color="var(--mantine-color-dimmed)" />
            </Group>
          </UnstyledButton>
        </Popover.Target>
        <Popover.Dropdown p="md">
          <Stack gap="sm">
            <div>
              <Text fw={750} fz="sm">
                Pick the model behind this page
              </Text>
              <Text fz="xs" c="dimmed" mt={4}>
                Every AI route runs on a default model chosen to balance cost and quality, but you can run it on any model you like. It's how you tune results: <b>Lite</b> models are the cheapest and quickest for high-volume calls, <b>Fast</b> models suit most logging, and <b>Pro</b> or <b>Frontier</b> models are more accurate on messy photos and vague text, but cost more and take longer.
              </Text>
            </div>
            <Box p="xs" style={{ background: "var(--pd-cream)", borderRadius: 8 }}>
              <Text fz={11} c="dimmed">
                In your app, send <code>?model=&lt;tag&gt;</code> on the call, or set the SDK's <code>model</code> option (it can differ per route). This demo keeps your choice in this browser. The <code>X-Model-Tag</code> response header shows which model answered.
              </Text>
            </Box>
            {routes.map((id) => (
              <ModelSelect key={id} routeId={id} label={aiRoute(id)?.title ?? id} value={models[id] ?? ""} onChange={(t) => setRouteModel(id, t)} inPopover />
            ))}
            <Group justify="space-between">
              <Anchor component={Link} to="/models" fz="xs" fw={600}>
                <Group gap={4}>
                  Compare every model <IconArrowRight size={12} />
                </Group>
              </Anchor>
              {custom.length > 0 && (
                <Button
                  variant="subtle"
                  color="gray"
                  size="compact-xs"
                  onClick={() => setRouteModels(Object.fromEntries(Object.entries(routeModels.get()).filter(([id]) => !routes.includes(id))))}
                >
                  Use route defaults
                </Button>
              )}
            </Group>
          </Stack>
        </Popover.Dropdown>
      </Popover>
      <ClientSettingsHint />
    </Stack>
  );
}
